import { ResourceConfigs } from "@/configs/resourceConfigs";
import { useStore } from "@/store";
import { Stack, Group, Space, Alert, Switch } from "@mantine/core";
import { Title } from "../ui/Title";
import {
  IconArrowLeft,
  IconExternalLink,
  IconEye,
  IconInfoCircle,
  IconListCheck,
  IconPlus,
  IconTrash,
  IconX,
} from "@tabler/icons-react";
import { ActionIcon } from "../ui/ActionIcon";
import { Button } from "../ui/Button";
import { TextInput } from "../ui/TextInput";
import { Text } from "../ui/Text";
import { Link } from "../ui/Link";
import {
  useActiveAssignment,
  useAssignmentPlaylists,
  useDidCheckStandaloneSeatAccess,
  useIsActiveAssignmentEmpty,
  useIsAssignmentUploadEnabled,
  useIsStandaloneSeatAccessLocked,
} from "@/store/selectors/assignmentSelectors";
import { AssignmentService } from "@/services/AssignmentService";
import { Textarea } from "../ui/Textarea";
import { useCallback, useMemo } from "react";
import { CopyPlaylistLink } from "../playlists/CopyPlaylistLink";
import { AssignmentList } from "./AssignmentList";
import { SegmentedControl } from "../ui/SegmentedControl";
import { AssignmentTypes } from "./assignmentConfigs";
import { useStyles } from "@/styles/useStyles";
import { CopyAssignmentEmailTemplate } from "./CopyAssignmentEmailTemplate";
import AssignmentQRCode from "./AssignmentQRCode";
import { NumberInput } from "../ui/NumberInput";
import { AssignmentPromptConfiguration } from "./AssignmentPromptConfiguration";
import { Analytics } from "@/services/Analytics";
import { Notifications } from "@/services/Notifications";
import { SupportVideoButton } from "../support/SupportVideoButton";
import { config } from "@/config";
import { AssignmentStepNumber } from "./AssignmentStepNumber";
import { Navigation } from "@/services/Navigation";
import { useActiveUser } from "@/store/selectors";

const spaceHeight = 50;

export function AssignmentManager() {
  const { theme } = useStyles();
  const activeUser = useActiveUser();

  const activeAssignment = useActiveAssignment();
  const assignmentPlaylists = useAssignmentPlaylists();
  const setActivePlaylistId = useStore(
    (state) => state.partnerApp.setActivePlaylistId,
  );
  const setActiveAssignmentId = useStore(
    (state) => state.partnerApp.setActiveAssignmentId,
  );

  const setIsAssignmentManagerOpen = useStore(
    (state) => state.partnerApp.setIsAssignmentManagerOpen,
  );

  const isActiveAssignmentEmpty = useIsActiveAssignmentEmpty();
  const isAssignmentUploadEnabled = useIsAssignmentUploadEnabled();
  const isStandaloneSeatAccessLocked = useIsStandaloneSeatAccessLocked();
  const didCheckStandaloneSeatAccess = useDidCheckStandaloneSeatAccess();

  const hasPartnerAssociation = Boolean(
    activeUser?.partnerData?.partnerId || activeUser?.partnerId,
  );
  const shouldDisableAssignmentDistribution =
    !hasPartnerAssociation &&
    (!didCheckStandaloneSeatAccess || isStandaloneSeatAccessLocked);

  const isAnyAdvizersAssignment = useMemo(() => {
    return activeAssignment?.assignmentType === "anyAdvizers";
  }, [activeAssignment?.assignmentType]);

  // const isAnyPlaylistsAssignment = useMemo(() => {
  //   return activeAssignment?.assignmentType === "anyPlaylists";
  // }, [activeAssignment?.assignmentType]);

  const isSpecificPlaylistsAssignment = useMemo(() => {
    return activeAssignment?.assignmentType === "specificPlaylists";
  }, [activeAssignment?.assignmentType]);

  // useEffect(() => {
  //   const isRootUrl = window.location.pathname === "/";
  //   const isPlaylistUrl = window.location.pathname.includes("/playlists");

  //   // if (!activeAssignment?.id) return;

  //   // console.log({
  //   //   isRootUrl,
  //   //   isPlaylistUrl,
  //   //   activeAssignmentId: activeAssignment?.id,
  //   //   activeAssignmentType: activeAssignment?.assignmentType,
  //   // });
  //   // if (activeAssignment?.assignmentType === "anyAdvizers" && isPlaylistUrl) {
  //   //   console.log("nav to root url");
  //   //   Navigation.navigate("/");
  //   // } else if (
  //   //   activeAssignment?.assignmentType !== "anyAdvizers" &&
  //   //   isRootUrl
  //   // ) {
  //   //   console.log("nav to playlist url");
  //   //   Navigation.navigate("/playlists");
  //   // } else {
  //   //   console.log("no nav");
  //   // }
  // }, []);

  const AssignedPlaylists = useMemo(() => {
    return (
      <Stack gap="sm">
        {assignmentPlaylists.map((playlist) => {
          return (
            <Group
              key={playlist.id}
              gap="xs"
              bg="background.0"
              p="sm"
              style={{
                borderRadius: theme.radius.md,
                border: `1px solid ${theme.colors.border[2]}`,
                boxShadow: theme.shadows.xs,
              }}
            >
              <Text
                titleStyle="h6"
                lh={1.2}
                fw={500}
                flex={1}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {playlist.title}
              </Text>
              <Group>
                <ActionIcon
                  radius="xl"
                  color="text.6"
                  onClick={() => setActivePlaylistId(playlist.id)}
                  h={24}
                  ariaLabel="Preview playlist"
                >
                  <IconEye size={16} />
                </ActionIcon>
                <ActionIcon
                  color="pink.6"
                  radius="xl"
                  onClick={() => {
                    if (!activeAssignment?.id) return;
                    AssignmentService.removePlaylistFromAssignment({
                      assignmentId: activeAssignment?.id,
                      playlistId: playlist.id,
                    });
                  }}
                  ariaLabel="Remove playlist from assignment"
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            </Group>
          );
        })}
      </Stack>
    );
  }, [
    activeAssignment?.id,
    assignmentPlaylists,
    setActivePlaylistId,
    theme.colors.border,
    theme.radius.md,
    theme.shadows.xs,
  ]);

  const AssignmentManagerHeader = useMemo(() => {
    return (
      <Group
        justify="space-between"
        align="center"
        w="100%"
        px="sm"
        pl={0}
        py="sm"
        wrap="nowrap"
      >
        {!activeAssignment ? (
          <Group align="center" justify="flex-start" gap="xs">
            <ResourceConfigs.assignment.Icon size={28} />
            <Title order={2} size="h3" mt={2}>
              My Assignments
            </Title>

            <Link
              to={config.assignmentGuideUrl}
              target="_blank"
              style={{
                marginLeft: 16,
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                opacity: 0.75,
              }}
            >
              Assignment Guide + FAQ{" "}
              <IconExternalLink size={16} style={{ marginLeft: 4 }} />
            </Link>
          </Group>
        ) : (
          <Group align="center" justify="flex-start" gap="xs">
            <Button
              variant="transparent"
              color="text.7"
              // color="blue.7"
              size="sm"
              radius="xl"
              leftSection={<IconArrowLeft size={20} />}
              onClick={() => {
                setActiveAssignmentId(null);
              }}
            >
              Manage Assignments
            </Button>

            <Link
              to={config.assignmentGuideUrl}
              target="_blank"
              style={{
                fontSize: 12,
                display: "flex",
                alignItems: "center",
                opacity: 0.75,
              }}
            >
              Assignment Guide + FAQ{" "}
              <IconExternalLink size={16} style={{ marginLeft: 4 }} />
            </Link>
          </Group>
        )}

        <ActionIcon
          ariaLabel="Close assignment manager"
          variant="light"
          size="md"
          radius="xl"
          color="text.6"
          style={{ zIndex: 100 }}
          onClick={() => {
            setIsAssignmentManagerOpen(false);
            setActiveAssignmentId(null);
          }}
        >
          <IconX size={20} />
        </ActionIcon>
      </Group>
    );
  }, [activeAssignment, setActiveAssignmentId, setIsAssignmentManagerOpen]);

  const SpecificPlaylists = useMemo(() => {
    if (!isSpecificPlaylistsAssignment) return null;
    return (
      <>
        <Group gap="xs" align="center">
          {/* <ResourceConfigs.playlist.Icon size={20} /> */}
          <Text lh={1.2} size="sm" fw={700}>
            <AssignmentStepNumber stepNumber={4} /> Add specific playlists to
            this assignment
          </Text>
        </Group>

        {!isActiveAssignmentEmpty ? (
          <>
            <Space h="sm" />
            {AssignedPlaylists}
          </>
        ) : (
          <>
            <Space h="sm" />
            <Group></Group>
            <Alert color="primary">
              <Group gap="xs">
                <IconArrowLeft size={16} />
                <Text size="sm">Click</Text>
                <Button
                  size="xs"
                  radius="xl"
                  px="xs"
                  mx={6}
                  h={24}
                  styles={{
                    label: { fontSize: 10 },
                  }}
                >
                  <IconPlus size={12} />
                  <Space w={4} />
                  Assign
                </Button>
                <Text size="sm" style={{ flex: 1 }}>
                  to add playlists to this assignment.
                </Text>
              </Group>
            </Alert>
          </>
        )}
      </>
    );
  }, [
    AssignedPlaylists,
    isActiveAssignmentEmpty,
    isSpecificPlaylistsAssignment,
  ]);

  const renderDeleteAssignmentButton = useCallback(() => {
    return (
      <Button
        variant="outline"
        mx="auto"
        color="red"
        radius="xl"
        size="xs"
        w="max-content"
        leftSection={<IconTrash size={16} />}
        onClick={() => {
          if (!activeAssignment?.id) return;
          const confirmed = confirm(
            "Are you sure you want to delete this assignment?",
          );
          if (!confirmed) return;
          AssignmentService.removeAssignment({
            assignmentId: activeAssignment?.id,
          });
          setActiveAssignmentId(null);

          Notifications.showNotification({
            title: "Assignment deleted",
            type: "success",
          });
        }}
      >
        Delete assignment
      </Button>
    );
  }, [activeAssignment?.id, setActiveAssignmentId]);

  const renderAssignmentActions = useCallback(
    (params: { stacked?: boolean } = {}) => {
      const { stacked = false } = params;
      const startingNumber =
        activeAssignment?.assignmentType === "specificPlaylists" ? 5 : 4;

      const renderPlaylistLink = () => {
        return (
          <CopyPlaylistLink
            propsLink={`${window.location.origin}/assignments/${activeAssignment?.id}`}
            context="Assignment Manager - Copy assignment link bottom"
            size={stacked ? "md" : "sm"}
            disabled={
              isActiveAssignmentEmpty || shouldDisableAssignmentDistribution
            }
            // fullWidth={params.stacked}
            text="Copy assignment link"
            onClick={(link) => {
              Analytics.assignmentCopyLink({
                assignmentId: activeAssignment?.id,
                assignmentTitle: activeAssignment?.title,
                url: link,
              });
            }}
          />
        );
      };

      const renderStudentViewButton = () => {
        return (
          <Link
            to={`/assignments/${activeAssignment?.id}?student=true`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              Analytics.assignmentOpenStudentView({
                assignmentId: activeAssignment?.id,
                assignmentTitle: activeAssignment?.title,
              });
            }}
          >
            <Button
              variant="light"
              color="primary"
              leftSection={<IconExternalLink size={16} />}
              radius="xl"
              size={stacked ? "sm" : "xs"}
              disabled={isActiveAssignmentEmpty}
              fullWidth={params.stacked}
            >
              {stacked ? "Go to student view" : `Student view`}
            </Button>
          </Link>
        );
      };

      const renderResultsButton = () => {
        return (
          <Link
            to={`/assignments/${activeAssignment?.id}`}
            target="_blank"
            rel="noopener noreferrer"
            disabled={shouldDisableAssignmentDistribution}
            onClick={() => {
              Analytics.assignmentOpenResultsView({
                assignmentId: activeAssignment?.id,
                assignmentTitle: activeAssignment?.title,
              });
            }}
          >
            <Button
              variant="light"
              color="primary"
              leftSection={<IconListCheck size={16} />}
              radius="xl"
              size={stacked ? "sm" : "xs"}
              disabled={
                isActiveAssignmentEmpty || shouldDisableAssignmentDistribution
              }
              fullWidth={params.stacked}
            >
              {stacked ? "View assignment results" : `View results`}
            </Button>
          </Link>
        );
      };

      if (stacked) {
        return (
          <Stack p={0} w="100%" gap={spaceHeight}>
            <Stack gap={4}>
              <Text size="sm" fw={700} mb="xs">
                <AssignmentStepNumber stepNumber={startingNumber + 1} /> Preview
                the assignment like a student sees it
              </Text>
              {renderStudentViewButton()}
            </Stack>
            <Stack gap={4}>
              <Text size="sm" fw={700} mb="xs">
                <AssignmentStepNumber stepNumber={startingNumber + 2} /> Send
                the assignment to your students
              </Text>
              {renderPlaylistLink()}
              <Space h={4} />
              {activeAssignment?.id && (
                <>
                  <CopyAssignmentEmailTemplate
                    assignmentId={activeAssignment?.id}
                    disabled={
                      isActiveAssignmentEmpty ||
                      shouldDisableAssignmentDistribution
                    }
                  />
                  <Stack w="100%" align="center">
                    <AssignmentQRCode
                      assignmentId={activeAssignment.id}
                      disabled={shouldDisableAssignmentDistribution}
                    />
                  </Stack>
                </>
              )}
            </Stack>
            <Stack gap={4}>
              <Text size="sm" fw={700} mb="xs">
                <AssignmentStepNumber stepNumber={startingNumber + 3} /> View
                assignment results in real-time
              </Text>

              {renderResultsButton()}
            </Stack>
          </Stack>
        );
      }

      return (
        <Group px="md" gap={"xs"}>
          {renderPlaylistLink()}
          {renderStudentViewButton()}
          {renderResultsButton()}
        </Group>
      );
    },
    [
      activeAssignment?.assignmentType,
      activeAssignment?.id,
      activeAssignment?.title,
      isActiveAssignmentEmpty,
      shouldDisableAssignmentDistribution,
    ],
  );

  const renderAssignmentCount = useCallback(() => {
    if (isSpecificPlaylistsAssignment) {
      return null;
    }

    return (
      <>
        <Space h={16} />
        <NumberInput
          id="assignment-count"
          title="Assignment count"
          label={`Minimum ${isAnyAdvizersAssignment ? "Advizers" : "playlists"} to watch`}
          styles={{
            label: {
              fontSize: 12,
              fontWeight: 700,
            },
          }}
          value={activeAssignment?.requiredCount || 1}
          size="sm"
          min={1}
          max={5}
          ml={6}
          onDebouncedChange={(value) => {
            if (!activeAssignment?.id) return;
            AssignmentService.updateAssignmentCount({
              assignmentId: activeAssignment?.id,
              count: value || 1,
            });
          }}
        />
      </>
    );
  }, [
    activeAssignment?.id,
    activeAssignment?.requiredCount,
    isAnyAdvizersAssignment,
    isSpecificPlaylistsAssignment,
  ]);

  const renderMaxTimeLimit = useCallback(() => {
    if (!isAnyAdvizersAssignment) {
      return null;
    }

    return (
      <>
        <Space h={16} />
        <NumberInput
          id="min-time-limit"
          title="Total minutes watched to complete assignment"
          label="Total minutes watched to complete assignment"
          value={activeAssignment?.totalMinutesToComplete || ""}
          placeholder={`Students must watch all videos for ${activeAssignment?.requiredCount} Advizers`}
          description="If set, the student will be required to watch at least this amount of minutes in total. If not set, they will need to watch every video for each Advizer."
          size="sm"
          max={120}
          ml={6}
          styles={{
            label: {
              fontSize: 12,
              fontWeight: 700,
            },
            description: {
              fontSize: 10,
              marginTop: 0,
            },
          }}
          onDebouncedChange={(value) => {
            if (!activeAssignment?.id) return;
            AssignmentService.updateAssignmentTotalMinutesToComplete({
              assignmentId: activeAssignment?.id,
              totalMinutesToComplete: value || null,
            });
          }}
        />
      </>
    );
  }, [
    activeAssignment?.id,
    activeAssignment?.requiredCount,
    activeAssignment?.totalMinutesToComplete,
    isAnyAdvizersAssignment,
  ]);

  const AssignmentUploadOptions = useMemo(() => {
    if (
      !isAssignmentUploadEnabled &&
      activeAssignment?.uploadType !== "careerLeader"
    )
      return null;

    return (
      <Stack>
        <Switch
          label="Enable recommendations based CareerLeader report"
          description="Students will be able to upload their CareerLeader report to get personalized recommendations."
          size="sm"
          checked={!!activeAssignment?.uploadType}
          onChange={(event) => {
            if (!activeAssignment?.id) return;
            AssignmentService.updateAssignmentUploadType({
              assignmentId: activeAssignment?.id,
              uploadType: event.currentTarget.checked ? "careerLeader" : null,
            });

            if (event.currentTarget.checked) {
              AssignmentService.updateAssignmentInstructions({
                assignmentId: activeAssignment?.id,
                instructions: `First, upload your CareerLeader report to get personalized career video recommendations (see your checklist below).\n\nThen watch videos that resonate with you the most. You can come back to the assignment page to check your progress.`,
              });
            } else {
              AssignmentService.updateAssignmentInstructions({
                assignmentId: activeAssignment?.id,
                instructions:
                  AssignmentTypes[activeAssignment?.assignmentType]
                    .defaultInstructions,
              });
            }
          }}
          styles={{
            label: {
              fontSize: 12,
            },
            description: {
              fontSize: 10,
              marginTop: 0,
            },
          }}
        />
      </Stack>
    );
  }, [
    activeAssignment?.assignmentType,
    activeAssignment?.id,
    activeAssignment?.uploadType,
    isAssignmentUploadEnabled,
  ]);

  const SelectAssignmentType = useMemo(() => {
    if (!activeAssignment?.id) return null;
    return (
      <Stack gap={0}>
        <Group justify="space-between" wrap="nowrap">
          <Text size="sm" fw={700}>
            <AssignmentStepNumber stepNumber={1} /> Pick an assignment type
          </Text>
          <SupportVideoButton supportVideoId="assignmentTypes" />
        </Group>
        <Space h={4} />
        <SegmentedControl
          ariaLabel="Pick an assignment type"
          size="xs"
          value={activeAssignment?.assignmentType}
          onChange={(value) => {
            const currentInstructions = activeAssignment?.instructions;
            const newAssignmentType = value as keyof typeof AssignmentTypes;
            const currentAssignmentRequiredCount =
              activeAssignment?.requiredCount;
            const newDefaultRequiredCount =
              AssignmentTypes[newAssignmentType].defaultRequiredCount;

            if (
              activeAssignment?.assignmentType &&
              currentInstructions ===
                AssignmentTypes[activeAssignment?.assignmentType]
                  .defaultInstructions
            ) {
              AssignmentService.updateAssignmentInstructions({
                assignmentId: activeAssignment?.id,
                instructions:
                  AssignmentTypes[newAssignmentType].defaultInstructions,
              });
            }

            AssignmentService.updateAssignmentType({
              assignmentId: activeAssignment?.id,
              assignmentType: value,
            });

            if (currentAssignmentRequiredCount !== newDefaultRequiredCount) {
              AssignmentService.updateAssignmentCount({
                assignmentId: activeAssignment?.id,
                count: newDefaultRequiredCount || 1,
                doNotTrack: true,
              });
            }

            switch (value) {
              case AssignmentTypes.anyAdvizers.id:
                Navigation.navigate("/");
                break;
              default:
                Navigation.navigate("/playlists");
                break;
            }
          }}
          data={[
            {
              label: AssignmentTypes.anyAdvizers.label,
              value: AssignmentTypes.anyAdvizers.id,
            },
            {
              label: AssignmentTypes.anyPlaylists.label,
              value: AssignmentTypes.anyPlaylists.id,
            },
            {
              label: AssignmentTypes.specificPlaylists.label,
              value: AssignmentTypes.specificPlaylists.id,
            },
          ]}
        />

        <Space h={6} />
        <Group gap={4} pl={4}>
          <Text
            size="xs"
            c="text.6"
            lh={1}
            style={{ display: "flex", alignItems: "center" }}
          >
            <IconInfoCircle
              size={14}
              style={{ color: theme.colors.text[6], marginRight: 4 }}
            />

            {AssignmentTypes[activeAssignment?.assignmentType]?.description({
              requiredCount: activeAssignment?.requiredCount || 0,
            })}
          </Text>
        </Group>

        {renderMaxTimeLimit()}

        {renderAssignmentCount()}

        {isAnyAdvizersAssignment && AssignmentUploadOptions && (
          <>
            <Space h="md" />
            {AssignmentUploadOptions}
          </>
        )}
      </Stack>
    );
  }, [
    AssignmentUploadOptions,
    activeAssignment?.assignmentType,
    activeAssignment?.id,
    activeAssignment?.instructions,
    activeAssignment?.requiredCount,
    isAnyAdvizersAssignment,
    renderAssignmentCount,
    renderMaxTimeLimit,
    theme.colors.text,
  ]);

  const AssignmentName = useMemo(() => {
    return (
      <TextInput
        id="assignment-name"
        title="Give your assignment a name"
        label={
          <>
            <AssignmentStepNumber stepNumber={2} /> Give your assignment a name
            <Space h="xs" />
          </>
        }
        value={activeAssignment?.title || ""}
        onDebouncedChange={(value) => {
          if (!activeAssignment?.id) return;
          AssignmentService.updateAssignmentTitle({
            assignmentId: activeAssignment?.id,
            title: value,
          });
          // updateEditorState({ prop: "name", value });
        }}
        placeholder={`e.g. "CHEM 200: Is a careers in medicine right for me?"`}
      />
    );
  }, [activeAssignment?.id, activeAssignment?.title]);

  const AssignmentInstructions = useMemo(() => {
    return (
      <Textarea
        id="assignment-instructions"
        title="Assignment Instructions"
        label={
          <>
            <AssignmentStepNumber stepNumber={3} /> Provide some instructions
            for your students
            <Space h="xs" />
          </>
        }
        value={activeAssignment?.instructions || ""}
        minRows={3}
        autosize
        onDebouncedChange={(value) => {
          if (!activeAssignment?.id) return;
          AssignmentService.updateAssignmentInstructions({
            assignmentId: activeAssignment?.id,
            instructions: value,
          });
        }}
        placeholder="Enter assignment instructions here"
      />
    );
  }, [activeAssignment?.id, activeAssignment?.instructions]);

  const AssignmentSessionScheduling = useMemo(() => {
    return (
      <TextInput
        id="assignment-session-scheduling"
        title="Paste your 1-on-1 session scheduling link"
        label="Paste your 1-on-1 session scheduling link"
        value={activeAssignment?.sessionSchedulingLink || ""}
        description="Encourage students to schedule a 1-on-1 session with you. Use their video choices and reflection responses to tailor the session."
        onDebouncedChange={(value) => {
          if (!activeAssignment?.id) return;
          AssignmentService.updateAssignmentSessionSchedulingLink({
            assignmentId: activeAssignment?.id,
            sessionSchedulingLink: value || null,
          });
        }}
        placeholder={`https://school.12twenty.com/appointments/new?...`}
      />
    );
  }, [activeAssignment?.id, activeAssignment?.sessionSchedulingLink]);

  return (
    <Stack px="sm" py="md" pt={0} pb={100} gap={0} w="100%">
      {AssignmentManagerHeader}

      {!activeAssignment?.id ? (
        <>
          <Space h="md" />
          <AssignmentList />
        </>
      ) : (
        <>
          <Space h="md" />
          <Group justify="space-between" wrap="nowrap" px="md">
            <Group align="flex-start" gap="xs" wrap="nowrap">
              <ResourceConfigs.assignment.Icon size={26} />
              <Title order={1} size="h3" lh={1.2} lineClamp={1} pt={2}>
                {activeAssignment?.title || "Untitled Assignment"}
              </Title>
            </Group>
          </Group>

          <Space h="xs" />
          <Group px="md" gap="sm">
            <Link
              to={`/assignments/${activeAssignment?.id}?student=true`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => {
                Analytics.assignmentOpenStudentView({
                  assignmentId: activeAssignment?.id,
                  assignmentTitle: activeAssignment?.title,
                });
              }}
            >
              <Button
                variant="light"
                color="primary"
                leftSection={<IconExternalLink size={12} />}
                radius="xl"
                fz={10}
                size="xs"
                disabled={isActiveAssignmentEmpty}
                h={24}
                py={0}
              >
                Student view
              </Button>
            </Link>
            <CopyPlaylistLink
              propsLink={`${window.location.origin}/assignments/${activeAssignment?.id}`}
              context="Assignment Manager - Copy assignment link top"
              size="xs"
              disabled={
                isActiveAssignmentEmpty || shouldDisableAssignmentDistribution
              }
              fullWidth={false}
              // fullWidth={params.stacked}
              text="Copy assignment link"
              onClick={(link) => {
                Analytics.assignmentCopyLink({
                  assignmentId: activeAssignment?.id,
                  assignmentTitle: activeAssignment?.title,
                  url: link,
                });
              }}
            />
            {activeAssignment?.id && (
              <AssignmentQRCode
                minimal
                assignmentId={activeAssignment.id}
                disabled={
                  isActiveAssignmentEmpty || shouldDisableAssignmentDistribution
                }
              />
            )}
            <Link
              to={`/assignments/${activeAssignment?.id}`}
              target="_blank"
              rel="noopener noreferrer"
              disabled={shouldDisableAssignmentDistribution}
              onClick={() => {
                Analytics.assignmentOpenResultsView({
                  assignmentId: activeAssignment?.id,
                  assignmentTitle: activeAssignment?.title,
                });
              }}
            >
              <Button
                variant="light"
                color="primary"
                leftSection={<IconListCheck size={12} />}
                radius="xl"
                size="xs"
                disabled={
                  isActiveAssignmentEmpty || shouldDisableAssignmentDistribution
                }
                fz={10}
                h={24}
                py={0}
              >
                View results
              </Button>
            </Link>
          </Group>

          <Space h="xl" />

          <Stack px="md" gap={0}>
            {SelectAssignmentType}

            <Space h={spaceHeight} />

            {AssignmentName}

            <Space h={spaceHeight} />

            {AssignmentInstructions}

            <Space h="sm" />

            {AssignmentSessionScheduling}

            <Space h={spaceHeight} />

            {SpecificPlaylists}

            {SpecificPlaylists && <Space h={spaceHeight} />}

            <AssignmentPromptConfiguration
              stepNumber={SpecificPlaylists ? 5 : 4}
            />

            <Space h={spaceHeight} />

            {renderAssignmentActions({ stacked: true })}

            <Space h={spaceHeight} />

            <Stack gap={4} align="center">
              <Text size="xs" fw={700}>
                Email us if you need help with anything!
              </Text>
              <Link to="mailto:dev@advizehub.com" style={{ fontSize: 14 }}>
                dev@advizehub.com
              </Link>
            </Stack>

            <Space h={50} />

            {renderDeleteAssignmentButton()}
          </Stack>
        </>
      )}
    </Stack>
  );
}
